// STARCODE - Three.js port of NEU.pde. Entry point: scene/renderer/composer setup, audio + MIDI
// bootstrapping, keyboard wiring and the main render loop.

import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";

import { ParticleSystem, PARTICLE_COUNT, PARTICLE_PRESETS } from "./ParticleSystem.js";
import { SunObject } from "./SunObject.js";
import { AudioAnalyser } from "./audio.js";
import { MidiManager } from "./midi.js";
import { AutoMix } from "./autoMix.js";
import { initRemoteSync, broadcastAction, broadcastCameraState } from "./remoteSync.js";
import { installKeybinds } from "./keybinds.js";
import { installControlBar } from "./controlBar.js";
import { state, toggleEffect, resetEffects } from "./state.js";
import { initI18n, getLang, setLang, t } from "./i18n.js";
import { createAboutModal, showAboutModal } from "./aboutModal.js";
import { getVisitorCount } from "./counter.js";

// Initialize internationalization & modals
initI18n();
createAboutModal();

// Fetch & update visitor count badge
getVisitorCount().then((count) => {
  const numEl = document.getElementById("visitorCountNum");
  if (numEl) {
    numEl.textContent = typeof count === "number" ? count.toLocaleString(getLang() === "de" ? "de-DE" : "en-US") : String(count);
  }
});

const WORD = "STARCODE";

// --- Regie/Output split -----------------------------------------------------------------
// Live mode hides every button for a clean Syphon/OBS feed - which raises the obvious question:
// how do you drive the show if you can't see the buttons? The answer is to open this same page
// TWICE: one window as the clean picture (?role=output, fullscreen, captured), one window kept
// on your own screen as the control surface (?role=control, full button/slider UI, never hidden,
// never captured). remoteSync.js keeps both windows' `state` in sync via BroadcastChannel.
// Opened with no `role` param at all, it behaves exactly as a normal single-window instance.
const role = new URLSearchParams(location.search).get("role"); // "control" | "output" | null
const isControlRole = role === "control";
let exitRegieBtn = null;
document.body.classList.toggle("controlRole", isControlRole);
if (isControlRole) {
  document.title = `STARCODE — ${getLang() === "en" ? "Control" : "Regie"}`;
  exitRegieBtn = document.createElement("button");
  exitRegieBtn.type = "button";
  exitRegieBtn.className = "exitRegieBtn";
  exitRegieBtn.textContent = t("exitRegie");
  exitRegieBtn.title = t("exitRegieTip");
  exitRegieBtn.addEventListener("click", () => {
    const u = new URL(location.href);
    u.searchParams.delete("role");
    u.searchParams.delete("live");
    location.href = u.pathname;
  });
  document.body.appendChild(exitRegieBtn);
}
// Distinct title for the output role too, so it's easy to pick out by name in a Syphon/OBS
// "capture this specific window" source list (as opposed to "capture entire screen", which is
// exactly the approach that forces a fullscreen/single-window trade-off - see openOutputWindow
// below and applyLiveModeVisual, which deliberately does NOT force fullscreen anymore).
if (role === "output") document.title = "STARCODE — Output";

const canvas = document.getElementById("scene");
const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: "high-performance",
  preserveDrawingBuffer: true,
});
// Keep the drawing buffer at the selected output resolution. CSS scales only the preview/window;
// changing popup size must never change the pixels delivered by canvas capture/Syphon bridges.
renderer.setPixelRatio(1);
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.1;

const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0x02030a, 0.00018);

const camera = new THREE.PerspectiveCamera(52, window.innerWidth / window.innerHeight, 0.5, 120000);
camera.position.set(0, 60, 780);

let autoCamBaseDistance = null;

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.06;
controls.autoRotate = true;
controls.autoRotateSpeed = 0.35;
controls.minDistance = 2;
controls.maxDistance = 100000;
// No angle limits at all: the camera may orbit a full 360 degrees horizontally and vertically,
// including straight over the poles. Only the zoom distance stays bounded.
controls.minAzimuthAngle = -Infinity;
controls.maxAzimuthAngle = Infinity;
controls.minPolarAngle = 0;
controls.maxPolarAngle = Math.PI;

// A faint starfield backdrop for depth/parallax - cheap (single Points draw call).
function buildStarfield() {
  const count = 3000;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = 1800 + Math.random() * 2200;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({ color: 0x4a5680, size: 2.2, sizeAttenuation: true, transparent: true, opacity: 0.5 });
  return new THREE.Points(geo, mat);
}
scene.add(buildStarfield());

const sunObject = new SunObject();
scene.add(sunObject.group);

const particleSystem = new ParticleSystem(renderer);
state.particleCount = PARTICLE_COUNT;
scene.add(particleSystem.points);

const composer = new EffectComposer(renderer);
composer.addPass(new RenderPass(scene, camera));
const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.25, 0.35, 0.22);
composer.addPass(bloomPass);
composer.addPass(new OutputPass());

function onResize() {
  const winW = window.innerWidth;
  let winH = window.innerHeight;

  if (state.viewportTopMode && !isControlRole) {
    const hud = document.getElementById("hud");
    const hudH = hud ? hud.getBoundingClientRect().height : 0;
    winH = Math.max(120, winH - hudH);
  }

  // Strict aspect ratio lock: prevents 3D stretching/distortion when integrated into Resolume,
  // OBS, or when resized on any monitor. The canvas always preserves the exact proportions of
  // the chosen format (e.g. 16:9 = 1.7777778), letterboxing/pillarboxing with black bars as needed.
  const targetW = state.outputWidth || 1920;
  const targetH = state.outputHeight || 1080;
  const targetAspect = targetW / targetH;
  const winAspect = winW / winH;

  let renderW, renderH;
  if (winAspect > targetAspect) {
    // Window is wider than target format -> Pillarbox (black bars on left & right)
    renderH = winH;
    renderW = Math.max(1, Math.round(winH * targetAspect));
  } else {
    // Window is taller than target format -> Letterbox (black bars on top & bottom)
    renderW = winW;
    renderH = Math.max(1, Math.round(winW / targetAspect));
  }

  // Camera projection is strictly locked to the target aspect ratio
  camera.aspect = targetAspect;
  camera.updateProjectionMatrix();

  // Internal WebGL Buffer is ALWAYS rendered at full target resolution (e.g. 1920x1080 / 4K),
  // regardless of how small the physical window is scaled on the user's screen!
  // This guarantees that Resolume / Syphon / OBS / PiP stream capture receives 100% full crisp resolution.
  const bufferW = isControlRole ? 64 : Math.min(3840, targetW);
  const bufferH = isControlRole ? 64 : Math.min(2160, targetH);
  renderer.setSize(bufferW, bufferH, false);
  composer.setSize(bufferW, bufferH);

  // Position canvas centered in window/viewport with CSS display dimensions
  renderer.domElement.style.width = `${renderW}px`;
  renderer.domElement.style.height = `${renderH}px`;
  renderer.domElement.style.position = "fixed";
  renderer.domElement.style.left = `${Math.round((winW - renderW) / 2)}px`;
  renderer.domElement.style.top = `${Math.round((winH - renderH) / 2)}px`;

  if (particleSystem.material?.uniforms?.uPixelRatio) {
    particleSystem.material.uniforms.uPixelRatio.value = Math.min(window.devicePixelRatio || 1, 2);
  }
}
window.addEventListener("resize", onResize);
onResize();

function resetCameraToSwarm() {
  const { center, radius } = particleSystem.getSwarmBounds();
  const viewDirection = new THREE.Vector3().subVectors(camera.position, controls.target);
  if (viewDirection.lengthSq() < 0.001) viewDirection.set(0, 0.08, 1);
  viewDirection.normalize();

  const halfFov = THREE.MathUtils.degToRad(camera.fov * 0.5);
  const fitDistance = THREE.MathUtils.clamp((radius / Math.sin(halfFov)) * 1.18, 320, 3600);

  state.cameraFollowOn = false;
  state.autoCamOn = false;
  state.cameraFlightMode = "manual";
  state.spin360On = false;
  autoCamBaseDistance = null;
  currentAudioRotSpeed = 0;

  controls.target.copy(center);
  camera.position.copy(center).addScaledVector(viewDirection, fitDistance);
  camera.up.set(0, 1, 0);
  camera.lookAt(center);
  controls.update();
  if (state.autofocusOn) {
    state.focusDistance = fitDistance;
  }
}

const audio = new AudioAnalyser();
const midi = new MidiManager({ particleSystem });
const autoMix = new AutoMix();
let remoteCameraSnapshot = null;

function captureCameraSnapshot() {
  if (isControlRole && remoteCameraSnapshot) return structuredClone(remoteCameraSnapshot);
  return {
    position: camera.position.toArray(),
    target: controls.target.toArray(),
    up: camera.up.toArray(),
    fov: camera.fov,
    zoom: camera.zoom,
  };
}

function applyCameraSnapshot(snapshot, { broadcast = true } = {}) {
  if (!snapshot?.position || !snapshot?.target) return;
  camera.position.fromArray(snapshot.position);
  controls.target.fromArray(snapshot.target);
  if (snapshot.up) camera.up.fromArray(snapshot.up);
  if (Number.isFinite(snapshot.fov)) camera.fov = snapshot.fov;
  if (Number.isFinite(snapshot.zoom)) camera.zoom = snapshot.zoom;
  camera.updateProjectionMatrix();
  controls.update();
  remoteCameraSnapshot = structuredClone(snapshot);
  if (broadcast && isControlRole) broadcastAction("applyCamera", snapshot);
}

function performResetAll() {
  resetEffects();
  particleSystem.setParticleCount(272144);

  camera.position.set(0, 60, 780);
  camera.up.set(0, 1, 0);
  camera.fov = 52;
  camera.zoom = 1;
  camera.updateProjectionMatrix();
  controls.target.set(0, 0, 0);
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.35;
  controls.update();

  autoCamBaseDistance = null;
  currentAudioRotSpeed = 0;
  lastAudioExplosionAt = -999;
  lastAudioExplosionBeat = -1;
  audio.beat = false;
  audio.beatPulse = 0;
  audio.bpm = 0;
  state.tapBpmValue = 0;
  tapTimes = [];
  window.dispatchEvent(new Event("resize"));
}

let tapTimes = [];
function handleTapBpm() {
  const now = performance.now();
  if (tapTimes.length > 0 && now - tapTimes[tapTimes.length - 1] > 2500) {
    tapTimes = [];
  }
  tapTimes.push(now);
  if (tapTimes.length >= 2) {
    if (tapTimes.length > 8) tapTimes.shift();
    const intervals = [];
    for (let i = 1; i < tapTimes.length; i++) {
      intervals.push(tapTimes[i] - tapTimes[i - 1]);
    }
    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const bpm = Math.round(60000 / avgInterval);
    if (bpm >= 40 && bpm <= 240) {
      audio.bpm = bpm;
      state.tapBpmValue = bpm;
      if (isControlRole) {
        broadcastAction("setBpm", bpm);
      }
    }
  }
}

initRemoteSync({
  particleSystem,
  onAction: (action, value) => {
    if (action === "toggleFullscreen") toggleFullscreen();
    if (action === "resetCamera") resetCameraToSwarm();
    if (action === "resetAll") performResetAll();
    if (action === "applyCamera") applyCameraSnapshot(value, { broadcast: false });
    if (action === "tapBpm") handleTapBpm();
    if (action === "setBpm") {
      audio.bpm = value;
      state.tapBpmValue = value;
    }
  },
  onCamera: (snapshot) => {
    if (isControlRole) remoteCameraSnapshot = snapshot;
  },
});

installKeybinds({
  particleSystem,
  onResetAll: () => {
    if (isControlRole) {
      performResetAll();
      broadcastAction("resetAll");
    } else {
      performResetAll();
    }
  },
  onMorphToggle: (isOn) => {
    hudMorphFlash(isOn);
  },
});

// --- Live / Resolume output mode ------------------------------------------------------------
// A browser can't publish a Syphon/Spout/NDI sender itself, and Resolume has no screen-capture
// source - so the picture is handed over by a bridge app (Syphon publisher, OBS+NDI, or OBS's
// virtual camera) that captures this window. All this mode has to do is make that captured
// window CLEAN: no control bar, no HUD, no mouse cursor, and fullscreen on the output display.
// See README.md ("Live output to Resolume Arena") for the bridge setup.
//
// `state.liveModeOn` is just data and is shared with a control window via remoteSync - but only
// the OUTPUT window is allowed to act on it (hide its own UI). A control window must always keep
// its own buttons visible, even while it's driving the output into live mode.
//
// IMPORTANT: this deliberately does NOT force browser Fullscreen anymore. Fullscreen used to be
// requested automatically here, which is exactly the trap that broke single-window use: it takes
// over the ENTIRE display, so if the output and control UI are the same window, the buttons are
// gone with no way to get them back short of Escape. Hiding the control bar/HUD/cursor via the
// `liveMode` CSS class is the part that actually matters for a clean Syphon/OBS feed - a Syphon
// or OBS "capture this window" source grabs exactly that window's content whether it's fullscreen
// or just a normal window sized/positioned anywhere on screen. Use openOutputWindow() below to get
// a second, permanently clean window instead of fighting fullscreen in a single window.
function applyLiveModeVisual() {
  if (isControlRole) return;
  const on = state.liveModeOn;
  document.body.classList.toggle("liveMode", on);
}

function setLiveMode(on) {
  if (on) state.viewportTopMode = true;
  state.liveModeOn = on;
  applyLiveModeVisual();
  window.dispatchEvent(new Event("resize"));
}

function toggleLiveMode() {
  setLiveMode(!state.liveModeOn);
}

function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch((err) => {
      console.warn("[STARCODE] Fullscreen-Anfrage fehlgeschlagen:", err);
    });
  } else {
    document.exitFullscreen().catch(() => {});
  }
}

function handleFullscreenToggle() {
  if (isControlRole) {
    broadcastAction("toggleFullscreen");
  } else {
    toggleFullscreen();
  }
}

// Double-click on canvas toggles fullscreen in output/live view
renderer.domElement.addEventListener("dblclick", () => {
  toggleFullscreen();
});

window.addEventListener("keydown", (e) => {
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  // Plain S only - Shift+S is a different binding (Gravity Wells, see keybinds.js) and must
  // NOT also flip live mode, or the control bar would silently reappear mid-show every time
  // that effect is toggled.
  if (!e.shiftKey && e.key.toLowerCase() === "s") toggleLiveMode();
  if (!e.shiftKey && (e.key.toLowerCase() === "f" || e.key === "F11")) toggleFullscreen();
  if (e.key === "0" || e.key === "Home") resetCameraToSwarm();
  if (e.key === "Escape") {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    if (state.liveModeOn) setLiveMode(false);
  }
});

// Explosion/RB-burst are one-shot particle-injection calls, not persistent state - so unlike
// every other button they can't just ride along on remoteSync's state-diff polling. A control
// window (no renderer of its own) broadcasts them explicitly instead of calling them locally.
function fireOneshot(action, value) {
  if (isControlRole) {
    if (action === "resetAll") performResetAll();
    broadcastAction(action, value || 180);
    return;
  }
  if (action === "explosion") particleSystem.triggerExplosion(90);
  if (action === "rbBurst") particleSystem.triggerRbBurst(90);
  if (action === "shockwave") particleSystem.triggerShockwave(180);
  if (action === "resetSwarm") particleSystem.resetSwarm();
  if (action === "resetAll") performResetAll();
  if (action === "tapBpm") handleTapBpm();
  if (action === "setBpm") {
    audio.bpm = value;
    state.tapBpmValue = value;
  }
}

let syphonPopup = null;
let syphonStream = null;
let syphonResizeTimer = null;

function lockSyphonPopupAspect(outputWindow, aspect, initialWidth, initialHeight) {
  const chromeWidth = Math.max(0, outputWindow.outerWidth - outputWindow.innerWidth);
  const chromeHeight = Math.max(0, outputWindow.outerHeight - outputWindow.innerHeight);
  let lastWidth = initialWidth;
  let lastHeight = initialHeight;
  let applyingSize = false;

  const resizeContentTo = (contentWidth, contentHeight) => {
    const maxWidth = Math.max(320, (outputWindow.screen.availWidth || contentWidth) - chromeWidth - 16);
    const maxHeight = Math.max(180, (outputWindow.screen.availHeight || contentHeight) - chromeHeight - 16);
    const scale = Math.min(1, maxWidth / contentWidth, maxHeight / contentHeight);
    const width = Math.max(320, Math.round(contentWidth * scale));
    const height = Math.max(180, Math.round(width / aspect));

    applyingSize = true;
    lastWidth = width;
    lastHeight = height;
    try {
      outputWindow.resizeTo(width + chromeWidth, height + chromeHeight);
    } catch (_) {}
    requestAnimationFrame(() => { applyingSize = false; });
  };

  const enforceAspect = () => {
    if (applyingSize || outputWindow.closed) return;
    const width = Math.max(1, outputWindow.innerWidth);
    const height = Math.max(1, outputWindow.innerHeight);
    const widthChangedMore = Math.abs(width - lastWidth) >= Math.abs(height - lastHeight);
    if (widthChangedMore) {
      resizeContentTo(width, width / aspect);
    } else {
      resizeContentTo(height * aspect, height);
    }
  };

  outputWindow.addEventListener("resize", () => {
    clearTimeout(syphonResizeTimer);
    syphonResizeTimer = setTimeout(enforceAspect, 60);
  });
  resizeContentTo(initialWidth, initialHeight);
}

// Mirror the existing render canvas into a frameless, always-on-top Document PiP window instead
// of starting a second simulation. The main Top Viewport and its controls remain fully usable.
async function openOutputWindow() {
  const w = state.outputWidth || 1920;
  const h = state.outputHeight || 1080;
  const maxDisplayW = Math.max(320, (window.screen.availWidth || w) - 32);
  const maxDisplayH = Math.max(240, (window.screen.availHeight || h) - 72);
  const displayScale = Math.min(1, maxDisplayW / w, maxDisplayH / h);
  const displayW = Math.max(320, Math.round(w * displayScale));
  const displayH = Math.max(180, Math.round(displayW * h / w));

  if (!window.documentPictureInPicture?.requestWindow) {
    window.alert(
      "Das rahmenlose Syphon-Fenster benötigt Google Chrome mit Document Picture-in-Picture."
    );
    return;
  }

  let outputWindow;
  try {
    outputWindow = await window.documentPictureInPicture.requestWindow({
      width: displayW,
      height: displayH,
    });
  } catch (error) {
    console.error("[STARCODE] Syphon Document-PiP konnte nicht geöffnet werden:", error);
    window.alert(
      "Das rahmenlose Syphon-Fenster konnte nicht geöffnet werden.\n\n" +
      "Bitte Google Chrome verwenden und Bild-in-Bild für diese Seite erlauben."
    );
    return;
  }

  syphonPopup = outputWindow;
  outputWindow.document.title = "STARCODE — Syphon Output";
  outputWindow.document.documentElement.style.cssText = "width:100%;height:100%;margin:0;background:#000;";
  outputWindow.document.body.style.cssText = "width:100%;height:100%;margin:0;overflow:hidden;background:#000;";

  let video = outputWindow.document.getElementById("starcodeSyphonMirror");
  if (!video) {
    video = outputWindow.document.createElement("video");
    video.id = "starcodeSyphonMirror";
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.style.cssText = "display:block;width:100vw;height:100vh;object-fit:contain;background:#000;";
    outputWindow.document.body.replaceChildren(video);
  }

  if (!syphonStream || syphonStream.getVideoTracks().every((track) => track.readyState === "ended")) {
    syphonStream = canvas.captureStream(60);
  }
  video.srcObject = syphonStream;
  video.play().catch((error) => {
    console.warn("[STARCODE] Syphon mirror playback failed:", error);
  });
  lockSyphonPopupAspect(outputWindow, w / h, displayW, displayH);
}

// Single-screen Syphon popup float: keeps a minimal always-on-top output window while
// the control UI stays in the main page, matching the known-good backup behavior.
let pipVideoEl = null;
async function toggleAlwaysOnTopPiP() {
  try {
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
      return;
    }
    if (!pipVideoEl) {
      pipVideoEl = document.createElement("video");
      pipVideoEl.muted = true;
      pipVideoEl.autoplay = true;
      pipVideoEl.playsInline = true;
      pipVideoEl.style.position = "fixed";
      pipVideoEl.style.width = "2px";
      pipVideoEl.style.height = "2px";
      pipVideoEl.style.opacity = "0.001";
      pipVideoEl.style.pointerEvents = "none";
      pipVideoEl.style.zIndex = "-99999";
      document.body.appendChild(pipVideoEl);
    }
    const stream = canvas.captureStream ? canvas.captureStream(60) : null;
    if (stream) {
      pipVideoEl.srcObject = stream;
      await pipVideoEl.play();
      await pipVideoEl.requestPictureInPicture();
    }
  } catch (err) {
    console.warn("[STARCODE] Picture-in-Picture float warning:", err);
  }
}

// On-screen buttons - the reliable, interference-free way to trigger every effect (some keyboard
// shortcuts collide with browser/OS-reserved keys depending on layout, buttons never do).
// MIDI stays owned by the output window only (see midi.start() below) - a control window skips
// the MIDI-Learn section entirely so the same controller input can't get double-processed.
const syncControlBar = installControlBar({
  particleSystem,
  audio,
  midi: isControlRole ? null : midi,
  onLiveModeToggle: toggleLiveMode,
  onFullscreenToggle: handleFullscreenToggle,
  onResetCamera: () => {
    resetCameraToSwarm();
    broadcastAction("resetCamera");
  },
  onOpenOutput: openOutputWindow,
  onPipToggle: toggleAlwaysOnTopPiP,
  onOneshot: fireOneshot,
  onObjLoaded: (objText, filename) => {
    return particleSystem.loadCustomObj(objText, filename);
  },
  onCaptureCamera: captureCameraSnapshot,
  onApplyCamera: applyCameraSnapshot,
  onSetParticleCount: (count) => particleSystem.setParticleCount(count),
  onMorphToggle: (isOn) => {
    hudMorphFlash(isOn);
  },
});

// --- Classic keyboard zoom (scroll wheel already zooms via OrbitControls by default; this adds
// a "+"/"-" hold-to-zoom control on top, closer to the U/Z zoom keys in NEU.pde). ---
let zoomInHeld = false;
let zoomOutHeld = false;
window.addEventListener("keydown", (e) => {
  if (e.key === "+" || e.key === "=") zoomInHeld = true;
  if (e.key === "-" || e.key === "_") zoomOutHeld = true;
});
window.addEventListener("keyup", (e) => {
  if (e.key === "+" || e.key === "=") zoomInHeld = false;
  if (e.key === "-" || e.key === "_") zoomOutHeld = false;
});
function applyKeyboardZoom(dt) {
  if (!zoomInHeld && !zoomOutHeld) return;
  const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
  const dist = offset.length();
  const speed = Math.max(160, dist * 1.4) * dt;
  const delta = zoomInHeld ? -speed : speed;
  const newDist = THREE.MathUtils.clamp(dist + delta, controls.minDistance, controls.maxDistance);
  offset.setLength(newDist);
  camera.position.copy(controls.target).add(offset);
}

// --- Hold "1" + mouse-drag => pure X/Y pan (no rotation)
// Hold "2" + mouse-drag up/down => zoom (tap "2" toggles Scale/Freq.)
// Hold "3" + mouse-drag => unrestricted 360° spin in all 3D directions (tap "3" toggles 360 Spin auto-rotation)
let threeHeld = false;
let twoHeld = false;
let oneHeld = false;
window.addEventListener("keydown", (e) => {
  if (e.key === "3") {
    threeHeld = true;
    document.getElementById("navBadge3")?.classList.add("active");
  }
  if (e.key === "2") {
    twoHeld = true;
    document.getElementById("navBadge2")?.classList.add("active");
  }
  if (e.key === "1") {
    oneHeld = true;
    document.getElementById("navBadge1")?.classList.add("active");
  }
});
window.addEventListener("keyup", (e) => {
  if (e.key === "3") {
    threeHeld = false;
    document.getElementById("navBadge3")?.classList.remove("active");
  }
  if (e.key === "2") {
    twoHeld = false;
    document.getElementById("navBadge2")?.classList.remove("active");
  }
  if (e.key === "1") {
    oneHeld = false;
    document.getElementById("navBadge1")?.classList.remove("active");
  }
});

let dragMode = null; // null | "zoom" | "pan" | "spin"
let dragLastX = 0;
let dragLastY = 0;
let dragWasAutoRotate = false;

function onDragPointerDown(e) {
  if (!threeHeld && !twoHeld && !oneHeld) return;
  dragMode = threeHeld ? "spin" : twoHeld ? "zoom" : "pan";
  dragLastX = e.clientX;
  dragLastY = e.clientY;
  dragWasAutoRotate = controls.autoRotate;
  controls.autoRotate = false;
  controls.enabled = false;
  e.preventDefault();
}

function onDragPointerMove(e) {
  if (!dragMode) return;
  const dx = e.clientX - dragLastX;
  const dy = e.clientY - dragLastY;
  dragLastX = e.clientX;
  dragLastY = e.clientY;

  if (dragMode === "spin") {
    // Completely unconstrained 3D orbital spin. Rotating around the camera's own right/up axes
    // (instead of spherical theta/phi) means there are no poles to clamp against, so the drag
    // can tumble endlessly in every direction. camera.up is carried along so OrbitControls
    // continues from the new orientation without snapping back.
    const rotSpeed = 0.007;
    const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    camera.matrixWorld.extractBasis(right, up, new THREE.Vector3());
    const rot = new THREE.Quaternion()
      .setFromAxisAngle(up, -dx * rotSpeed)
      .multiply(new THREE.Quaternion().setFromAxisAngle(right, -dy * rotSpeed));
    offset.applyQuaternion(rot);
    camera.up.applyQuaternion(rot).normalize();
    camera.position.copy(controls.target).add(offset);
    camera.lookAt(controls.target);
  } else if (dragMode === "zoom") {
    const offset = new THREE.Vector3().subVectors(camera.position, controls.target);
    const dist = offset.length();
    const newDist = THREE.MathUtils.clamp(dist + dy * dist * 0.0035, controls.minDistance, controls.maxDistance);
    offset.setLength(newDist);
    camera.position.copy(controls.target).add(offset);
  } else if (dragMode === "pan") {
    const dist = camera.position.distanceTo(controls.target);
    const panSpeed = dist * 0.0016;
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    const forward = new THREE.Vector3();
    camera.matrixWorld.extractBasis(right, up, forward);
    const shift = right.multiplyScalar(-dx * panSpeed).add(up.multiplyScalar(dy * panSpeed));
    camera.position.add(shift);
    controls.target.add(shift);
  }
}

function onDragPointerUp() {
  if (!dragMode) return;
  dragMode = null;
  controls.enabled = true;
  controls.autoRotate = dragWasAutoRotate;
}

// Capture phase so this runs (and can disable `controls`) before OrbitControls' own listener.
renderer.domElement.addEventListener("pointerdown", onDragPointerDown, { capture: true });
window.addEventListener("pointermove", onDragPointerMove);
window.addEventListener("pointerup", onDragPointerUp);

// --- HUD & Top Navigation ---------------------------------------------------
const fpsEl = document.getElementById("fps");
const hudParticleSelect = document.getElementById("hudParticleSelect");
const audioStateEl = document.getElementById("audioState");
const midiStateEl = document.getElementById("midiState");
const beatStateEl = document.getElementById("beatState");
const sceneStateEl = document.getElementById("sceneState");
const liveStateEl = document.getElementById("liveState");

if (audioStateEl) {
  audioStateEl.style.cursor = "pointer";
  audioStateEl.title = "Klicken, um Mikrofonzugriff anzufordern / neu zu verbinden";
  audioStateEl.addEventListener("click", async () => {
    if (!audio.usingMic) {
      if (typeof window !== "undefined" && !window.isSecureContext && location.hostname !== "localhost" && location.hostname !== "127.0.0.1") {
        alert(getLang() === "de"
          ? "⚠️ HTTPS erforderlich: Web-Browser (Chrome, Safari, Firefox) erlauben Mikrofon-Zugriff online aus Sicherheitsgründen NUR über eine verschlüsselte HTTPS-Verbindung (https://).\n\nBitte rufe die Website mit https:// auf oder aktiviere ein SSL-Zertifikat auf deinem Server."
          : "⚠️ HTTPS required: Web browsers strictly require a secure HTTPS (https://) connection to access the microphone online.\n\nPlease open this site via https:// or enable SSL on your web server.");
        return;
      }
      audioStateEl.textContent = "Audio: Verbinde Mic...";
      const ok = await audio.requestMic();
      if (!ok) {
        alert(getLang() === "de" 
          ? "Mikrofon-Zugriff nicht erteilt oder blockiert. Bitte in den Browser-Einstellungen erlauben (Klick auf das Schloss/Website-Symbol in der Adressleiste)." 
          : "Microphone permission denied or blocked. Please allow mic access in your browser settings (click lock/settings icon in address bar).");
      }
    }
  });
}

function updateParticleCountDisplay() {
  if (!hudParticleSelect) return;
  if (hudParticleSelect.options.length === 0) {
    for (const preset of PARTICLE_PRESETS) {
      const option = document.createElement("option");
      option.value = String(preset.count);
      option.textContent = preset.label;
      hudParticleSelect.appendChild(option);
    }
  }
  hudParticleSelect.value = String(state.particleCount || PARTICLE_COUNT);
}
updateParticleCountDisplay();

if (hudParticleSelect) {
  hudParticleSelect.addEventListener("change", () => {
    particleSystem.setParticleCount(Number(hudParticleSelect.value));
  });
}

function installHudWidthResize() {
  const root = document.documentElement;
  const leftHandle = document.getElementById("hudResizeLeft");
  const rightHandle = document.getElementById("hudResizeRight");
  const storedWidth = Number(localStorage.getItem("starcode.ui.hudWidth"));
  if (Number.isFinite(storedWidth) && storedWidth > 0) {
    root.style.setProperty("--hud-content-width", `${Math.min(storedWidth, window.innerWidth - 16)}px`);
  }

  const installHandle = (handle, direction) => {
    if (!handle) return;
    handle.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      handle.setPointerCapture(event.pointerId);
      const startX = event.clientX;
      const startWidth = document.getElementById("controlBar")?.getBoundingClientRect().width || 1400;
      document.body.style.userSelect = "none";

      const onMove = (moveEvent) => {
        const delta = direction === "left" ? startX - moveEvent.clientX : moveEvent.clientX - startX;
        const width = THREE.MathUtils.clamp(startWidth + delta * 2, 900, window.innerWidth - 16);
        root.style.setProperty("--hud-content-width", `${width}px`);
        localStorage.setItem("starcode.ui.hudWidth", String(Math.round(width)));
        window.dispatchEvent(new Event("resize"));
      };
      const onUp = () => {
        document.body.style.userSelect = "";
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp, { once: true });
    });
    handle.addEventListener("dblclick", () => {
      root.style.removeProperty("--hud-content-width");
      localStorage.removeItem("starcode.ui.hudWidth");
      window.dispatchEvent(new Event("resize"));
    });
  };

  installHandle(leftHandle, "left");
  installHandle(rightHandle, "right");
}
installHudWidthResize();

function syncLanguageButtons() {
  const cur = getLang();
  document.querySelectorAll(".langBtn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.lang === cur);
  });
}

document.querySelectorAll(".langBtn").forEach((btn) => {
  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    setLang(btn.dataset.lang);
  });
});

const startAboutBtn = document.getElementById("startAboutBtn");
if (startAboutBtn) {
  startAboutBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    showAboutModal("about");
  });
}

const hudBrand = document.getElementById("hudBrand");
if (hudBrand) {
  hudBrand.addEventListener("click", (e) => {
    e.stopPropagation();
    showAboutModal("about");
  });
}

const hudFindSwarmBtn = document.getElementById("hudFindSwarmBtn");
if (hudFindSwarmBtn) {
  hudFindSwarmBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    resetCameraToSwarm();
    broadcastAction("resetCamera");
  });
}

const hudSyphonBtn = document.getElementById("hudSyphonBtn");
if (hudSyphonBtn) {
  hudSyphonBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleAlwaysOnTopPiP();
  });
}

const hudViewportBtn = document.getElementById("hudViewportBtn");
if (hudViewportBtn) {
  hudViewportBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleEffect("viewportTopMode");
    window.dispatchEvent(new Event("resize"));
  });
}

const hudAboutBtn = document.getElementById("hudAboutBtn");
if (hudAboutBtn) {
  hudAboutBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    showAboutModal("about");
  });
}

window.addEventListener("starcode:langchange", () => {
  syncLanguageButtons();
  updateParticleCountDisplay();
  if (isControlRole) {
    document.title = `STARCODE — ${getLang() === "en" ? "Control" : "Regie"}`;
    if (exitRegieBtn) {
      exitRegieBtn.textContent = t("exitRegieBtn");
      exitRegieBtn.title = t("exitRegieTitle");
    }
  }
});
syncLanguageButtons();

function hudMorphFlash(isOn) {
  document.title = isOn ? "STARCODE ✦" : "STARCODE";
}

let fpsFrames = 0;
let fpsAccum = 0;

function updateHud(dt) {
  fpsFrames++;
  fpsAccum += dt;
  if (fpsAccum >= 0.5) {
    fpsEl.textContent = `${Math.round(fpsFrames / fpsAccum)} ${t("fps")}`;
    fpsFrames = 0;
    fpsAccum = 0;
  }
  if (isControlRole) {
    audioStateEl.textContent = t("regieWindow");
  } else {
    const src = audio.usingMic ? (getLang() === "en" ? "Mic" : "Mikrofon") : (getLang() === "en" ? "Synthetic" : "synthetisch");
    const bars = Math.round(Math.min(1.0, audio.level) * 8);
    const meter = "█".repeat(bars) + "·".repeat(8 - bars);
    audioStateEl.textContent = `Audio: ${src} [${meter}]`;
  }
  midiStateEl.textContent = state.midiConnected ? t("midiConnected") : "";
  beatStateEl.textContent = audio.bpm ? `~${audio.bpm} BPM` : "";
  sceneStateEl.textContent = state.autoMixOn && autoMix.currentSceneName ? `${t("autoMixPrefix")}${autoMix.currentSceneName}` : "";
  liveStateEl.textContent = state.liveModeOn ? t("liveBadge") : "";
  if (hudParticleSelect && hudParticleSelect.value !== String(state.particleCount)) {
    hudParticleSelect.value = String(state.particleCount);
  }
  if (hudViewportBtn) hudViewportBtn.classList.toggle("active", !!state.viewportTopMode);
}

// --- Start overlay / bootstrap ---------------------------------------------
const startOverlay = document.getElementById("startOverlay");
const startButton = document.getElementById("startButton");

let isBooting = false;

// MediaStream Keep-Alive Pipeline: prevents Chrome / macOS from freezing the window or Syphon frame when Resolume is in focus
function startCanvasMediaKeepAlive() {
  try {
    if (!canvas || typeof canvas.captureStream !== "function") return;
    const stream = canvas.captureStream(60);
    const video = document.createElement("video");
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.srcObject = stream;
    video.style.position = "fixed";
    video.style.width = "2px";
    video.style.height = "2px";
    video.style.opacity = "0.001";
    video.style.pointerEvents = "none";
    video.style.zIndex = "-9999";
    document.body.appendChild(video);
    video.play().catch(() => {});
  } catch (e) {
    console.warn("[STARCODE] Media stream keepalive warning:", e);
  }
}

async function boot() {
  if (isBooting || state.audioReady) return;
  isBooting = true;
  if (role === "output" || new URLSearchParams(location.search).get("live") === "1") {
    startOverlay.classList.add("hidden");
    startOverlay.style.display = "none";
    setLiveMode(true);
  }
  // A control window never renders and never needs its own mic/MIDI access - it only drives the
  // output window's state via remoteSync - so it can skip straight past all of that and boot
  // near-instantly (also means no separate microphone-permission prompt on that machine/window).
  const tasks = isControlRole
    ? []
    : [audio.start(), midi.start(), particleSystem.loadMorphTarget(WORD)];
  await Promise.all(tasks);
  state.audioReady = true;
  startOverlay.classList.add("hidden");
  startOverlay.style.display = "none";
  particleSystem.startIntro();
  startCanvasMediaKeepAlive();

  // ?live=1 boots straight into clean output - handy for a dedicated Resolume capture window
  // that should never show UI, even for a frame.
  if (new URLSearchParams(location.search).get("live") === "1") setLiveMode(true);
}

startButton.addEventListener("click", () => {
  boot();
});

// Auto-boot if opened as dedicated output window or with ?live=1 so Resolume / Syphon
// captures live visuals immediately without waiting for extra clicks.
if (role === "output" || new URLSearchParams(location.search).get("live") === "1") {
  startOverlay.classList.add("hidden");
  startOverlay.style.display = "none";
  setLiveMode(true);
  boot();
}

// --- Main loop & background-safe render clock --------------------------------
// Standard requestAnimationFrame is throttled down or paused by macOS / Chromium when a window is
// not in the foreground (e.g. while Resolume Arena or OBS is the active focused application).
// We combine continuous requestAnimationFrame with an AudioContext / Worker interval heartbeat
// to ensure rendering NEVER drops below 60-120 fps even when completely in the background.
let lastFrameTime = performance.now();
let currentAudioRotSpeed = 0.0;
let lastAudioExplosionAt = -999;
let lastAudioExplosionBeat = -1;
let lastCameraBroadcastAt = -999;
let lastViewportTopMode = false;
const clock = new THREE.Clock();

function renderFrame() {
  const now = performance.now();
  const elapsed = (now - lastFrameTime) / 1000;
  if (elapsed < 0.007) return; // Cap at ~120 fps
  lastFrameTime = now;
  const dt = Math.min(elapsed, 0.05);

  // Keep the start screen responsive: the canvas is fully covered here, so running the
  // 272k-particle GPGPU simulation and HUD synchronization before boot only wastes frame time.
  if (!state.audioReady && !isControlRole) return;

  // The control window has no visible canvas at all (see style.css `.controlRole #scene`), so
  // skip every bit of GPGPU simulation and rendering work for it entirely - it's pure UI, driving
  // the actual output window over remoteSync instead.
  if (!isControlRole) {
    if (state.viewportTopMode !== lastViewportTopMode) {
      lastViewportTopMode = state.viewportTopMode;
      onResize();
    }
    audio.update(dt, state.audioNoiseGate ?? 0.04);
    autoMix.update(dt, audio);

    if (state.explosionAudioOn && audio.beat && audio.beatCount !== lastAudioExplosionBeat) {
      const sinceExplosion = particleSystem.clockTime - lastAudioExplosionAt;
      const energy = audio.sub * 0.28 + audio.bass * 0.44 + audio.low * 0.18 + audio.flux * 0.36;
      const minEnergy = Math.max(0.10, (state.audioNoiseGate ?? 0.04) * 1.1);
      const dynamicCooldown = THREE.MathUtils.lerp(1.35, 0.48, Math.min(1.0, energy));
      if (sinceExplosion >= dynamicCooldown && energy > minEnergy) {
        const strength = THREE.MathUtils.clamp(32 + energy * 78, 32, 92);
        particleSystem.triggerExplosion(strength);
        lastAudioExplosionAt = particleSystem.clockTime;
      }
      lastAudioExplosionBeat = audio.beatCount;
    }

    const isSunActive = !!(state.sunOn || state.fireballOn);
    const currentSunScale = sunObject.update(dt, audio, isSunActive, camera);
    particleSystem.setSunScale(currentSunScale);
    particleSystem.update(dt, audio);

    // Audio-reactive camera rotation: a high-contrast musical envelope creates clearly audible
    // acceleration on kicks/transients and a slower cinematic release between events.
    let targetRotSpeed = state.rotationSpeed;
    if (state.rotationAudioReact) {
      const gain = state.audioGain ?? 1.0;
      const userSlider = Math.max(0, state.rotationSpeed);
      const body = audio.sub * 0.22 + audio.bass * 0.34 + audio.lowMid * 0.18 + audio.highMid * 0.12 + audio.treble * 0.14;
      const transient = Math.min(1.5, audio.flux * 1.35 + audio.beatPulse * 1.2 + (audio.beat ? 0.8 : 0));
      const audioEnergy = Math.min(1.5, (body + transient * 0.38) * gain);
      const noiseThreshold = Math.max(0.025, (state.audioNoiseGate ?? 0.04) * 0.8);

      let targetSpeed = 0.0;
      if (userSlider > 0 && audioEnergy > noiseThreshold) {
        const normalized = Math.min(1.5, (audioEnergy - noiseThreshold) / Math.max(0.1, 1.0 - noiseThreshold));
        const groove = Math.pow(normalized, 1.55) * userSlider * 1.8;
        const beatKick = transient * userSlider * 1.65;
        targetSpeed = Math.min(userSlider * 4.2, groove + beatKick);
      }

      const easeRate = targetSpeed > currentAudioRotSpeed ? 11.0 : 1.65;
      currentAudioRotSpeed += (targetSpeed - currentAudioRotSpeed) * Math.min(1.0, dt * easeRate);
      targetRotSpeed = currentAudioRotSpeed;
    }

    const ROT_FACTOR = 5.5;
    const isIntro = particleSystem.isIntroRunning();
    if (isIntro) {
      // Keep camera rock-solid facing the STARCODE lettering during startup intro
      controls.autoRotate = false;
    } else if (state.spin360On) {
      controls.autoRotate = state.rotationAudioReact ? (targetRotSpeed > 0.005) : true;
      controls.autoRotateSpeed = state.rotationAudioReact
        ? targetRotSpeed * ROT_FACTOR
        : (state.rotationSpeed > 0.01 ? state.rotationSpeed * ROT_FACTOR : 2.5);
    } else {
      controls.autoRotate = state.rotationAudioReact ? (targetRotSpeed > 0.005) : (state.rotationSpeed > 0.001);
      controls.autoRotateSpeed = targetRotSpeed * ROT_FACTOR;
    }

    // Bloom is a live-performance parameter (slider + MIDI-assignable), so push it every frame.
    bloomPass.strength = state.bloomAmount;
    bloomPass.threshold = 0.20;

    // Autofocus: dynamically track camera-to-subject focal distance in real time
    if (state.autofocusOn) {
      const targetDist = camera.position.distanceTo(controls.target);
      state.focusDistance += (targetDist - state.focusDistance) * Math.min(1, dt * 6.0);
    }

    // Camera Follow Swarm: smooth 3D camera tracking to keep swarm center centered without frame stutter
    if (state.cameraFollowOn) {
      const swarmCenter = particleSystem.getSwarmCenter();
      const delta = new THREE.Vector3().subVectors(swarmCenter, controls.target);
      const lerpFactor = Math.min(1.0, dt * 2.2);
      const shift = delta.multiplyScalar(lerpFactor);
      controls.target.add(shift);
      camera.position.add(shift);
    }

    // AUTOCAM: automatic Z-axis camera flight towards particle swarm with user-defined stroke travel distance
    if (state.autoCamOn) {
      const targetPos = controls.target;
      const camPos = camera.position;
      const dir = new THREE.Vector3().subVectors(targetPos, camPos);
      const dist = dir.length();

      // When autoCam is engaged, capture the baseline distance where the user had the camera positioned
      if (autoCamBaseDistance === null) {
        autoCamBaseDistance = Math.max(160, dist);
      }

      const travelStroke = Math.max(50, state.autoCamDistance ?? 600);
      const minArrivalDist = Math.max(60, autoCamBaseDistance - travelStroke);
      const camVelocity = state.autoCamSpeed ?? 1.0;
      const audioSpeedBoost = state.rotationAudioReact ? (1.0 + (audio?.bass || 0) * 0.8) : 1.0;
      const flySpeed = (150.0 * camVelocity + (state.speedScale - 1.0) * 60.0) * audioSpeedBoost * dt;

      if (dist > minArrivalDist + 4.0) {
        dir.normalize();
        camPos.addScaledVector(dir, Math.min(flySpeed, dist - minArrivalDist));
      } else {
        // Smoothly loop/reset back out to the baseline starting distance to begin the next flight cycle
        const backOffset = new THREE.Vector3().subVectors(camPos, targetPos).normalize().multiplyScalar(autoCamBaseDistance);
        camPos.copy(targetPos).add(backOffset);
      }
    } else {
      autoCamBaseDistance = null;
    }

    // Camera Flight Paths (Seamless & Continuous: 360 Orbit, Spirale, Achter-Schleife ∞, Tunnel)
    if (state.cameraFlightMode && state.cameraFlightMode !== "none" && state.cameraFlightMode !== "manual") {
      controls.autoRotate = false;
      const mode = state.cameraFlightMode;
      const flightSpeed = 0.35 * (state.speedScale || 1.0);
      const time = clock.getElapsedTime() * flightSpeed;
      const targetFlightPos = new THREE.Vector3();

      if (mode === "orbit") {
        targetFlightPos.set(
          Math.sin(time * 0.8) * 780,
          70 + Math.sin(time * 0.4) * 110,
          Math.cos(time * 0.8) * 780
        );
      } else if (mode === "spiral") {
        const r = 680 + Math.sin(time * 0.4) * 140;
        targetFlightPos.set(
          Math.sin(time * 0.9) * r,
          Math.sin(time * 0.45) * 280 + 50,
          Math.cos(time * 0.9) * r
        );
      } else if (mode === "figure8") {
        // True 3D Lissajous Lemniscate (seamless C-infinity infinity loop with zero gimbal lock & zero jumps)
        const t = time * 0.7;
        targetFlightPos.set(
          Math.sin(t) * 760,
          Math.sin(2.0 * t) * 160 + 60,
          Math.cos(t) * 640
        );
      } else if (mode === "tunnelFly") {
        // Continuous sinusoidal shuttle flight through the core with zero modulo jumps
        const t = time * 0.5;
        targetFlightPos.set(
          Math.sin(t * 2.0) * 85,
          Math.cos(t * 2.0) * 65 + 30,
          Math.sin(t) * 950
        );
      }

      // Smooth interpolation into and along the flight trajectory to eliminate any abrupt snaps
      camera.position.lerp(targetFlightPos, Math.min(1.0, dt * 4.0));
      camera.lookAt(controls.target);
    }

    applyKeyboardZoom(dt);
    controls.update();
    if (particleSystem.clockTime - lastCameraBroadcastAt >= 0.25) {
      broadcastCameraState(captureCameraSnapshot());
      lastCameraBroadcastAt = particleSystem.clockTime;
    }
    if (state.blackoutActive) {
      renderer.setClearColor(0x000000, 1.0);
      renderer.clear();
    } else if (state.muteLocalRender) {
      renderer.clear();
    } else {
      composer.render();
    }
  }

  // Runs on both roles: reacts to state.liveModeOn regardless of whether it changed locally or
  // arrived from the other window over remoteSync.
  applyLiveModeVisual();
  updateHud(dt);
  syncControlBar();
}

function animateRAF() {
  requestAnimationFrame(animateRAF);
  renderFrame();
}
requestAnimationFrame(animateRAF);

// Background Web Worker Ticker (unthrottled by browser tab occlusion / background state)
try {
  const tickerCode = `
    let timer = null;
    self.onmessage = (e) => {
      if (e.data === "start") {
        if (!timer) timer = setInterval(() => self.postMessage("tick"), 1000 / 120);
      } else if (e.data === "stop") {
        clearInterval(timer);
        timer = null;
      }
    };
  `;
  const blob = new Blob([tickerCode], { type: "application/javascript" });
  const tickerWorker = new Worker(URL.createObjectURL(blob));
  tickerWorker.onmessage = () => {
    renderFrame();
  };
  tickerWorker.postMessage("start");
} catch (err) {
  console.warn("[STARCODE] Worker ticker unavailable:", err);
}

// Additional AudioContext ScriptProcessor / timer heartbeat for background keep-alive
try {
  const silentCtx = new (window.AudioContext || window.webkitAudioContext)();
  const osc = silentCtx.createOscillator();
  const gain = silentCtx.createGain();
  gain.gain.value = 0; // completely silent
  osc.connect(gain);
  gain.connect(silentCtx.destination);
  osc.start();
  setInterval(() => {
    if (silentCtx.state === "suspended") silentCtx.resume().catch(() => {});
    renderFrame();
  }, 1000 / 60);
} catch (_) {}
